import { ArrowIcon } from "assets/icons";
import cn from "classnames";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Input, Loader, Modal } from "ui";

import { Link } from "react-router-dom";

import { ManualReturnOrder } from "../../aiTickets.types";
import { useManualReturnContext } from "../../apis";
import { CreateReturnModal } from "../CreateReturnModal";

import styles from "./ManualReturnFlow.module.scss";

interface Props {
  conversationId: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Starting a return from the conversation instead of from a ticket — for when
 * the assistant is down, or simply did not read the complaint as a return.
 *
 * It creates the same kind of ticket the assistant would have, then hands over
 * to the same form. Nothing about the return itself is different; what differs
 * is who decided it was one, which the ticket records.
 */
export const ManualReturnFlow = ({ conversationId, isOpen, onClose }: Props) => {
  const { t } = useTranslation();

  const { data: context, isLoading } = useManualReturnContext(
    isOpen ? conversationId : null,
  );

  const [orderId, setOrderId] = useState("");
  const [reason, setReason] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setOrderId("");
      setReason("");
      setIsFormOpen(false);
    }
  }, [isOpen]);

  // Second step. Nothing has been written yet — the form itself creates the
  // return in one call, so abandoning here leaves no trace.
  if (isFormOpen) {
    return (
      <CreateReturnModal
        source={{ kind: "manual", conversationId, orderId }}
        title={t("Create return manually")}
        defaultReason={reason}
        defaultDescription={null}
        isOpen={isOpen}
        onClose={onClose}
      />
    );
  }

  const canSubmit = !!orderId && reason.trim().length >= 3;

  // The order code carries the row; everything else is context and goes on a
  // quieter second line, so a card never has to grow sideways to fit.
  const orderMeta = (order: ManualReturnOrder): string =>
    [
      order.deliveredAt
        ? t("delivered {{date}}", {
            date: moment(order.deliveredAt).format("DD/MM/YYYY"),
          })
        : null,
      order.itemCount === 1
        ? t("1 item")
        : t("{{count}} items", { count: order.itemCount }),
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <Modal
      isOpen={isOpen}
      title={t("Create return manually")}
      okText={t("Continue")}
      isOkDisabled={!canSubmit}
      className={styles.manualReturn_dialog}
      overlayClassName={styles.manualReturn_overlay}
      onClose={onClose}
      onOk={() => setIsFormOpen(true)}
    >
      {isLoading || !context ? (
        <Loader />
      ) : (
        <div className={styles.manualReturn_body}>
          {/* The safeguard, phrased as a confirmation rather than an obstacle:
              this path exists precisely so a return can be raised without the
              assistant, and it must not read as though the assistant is a
              prerequisite. Shown only when there is no return ticket here — if
              there is one, the agent is continuing its work, not overriding it. */}
          {!context.hasReturnTicket && (
            <div className={styles.manualReturn_notice}>
              {t(
                "The AI didn't detect any return intent in this conversation. Are you sure you want to create a return?",
              )}
            </div>
          )}

          <div className={styles.manualReturn_sectionTitle}>
            {t("Which order is being returned?")}
          </div>

          {context.orders.length === 0 ? (
            <div className={styles.manualReturn_empty}>
              {t("This customer has no delivered order that could be returned.")}
            </div>
          ) : (
            context.orders.map((order) => {
              const selectable = order.isWithinDeadline;

              return (
                <div
                  key={order.orderId}
                  className={cn(styles.manualReturn_card, {
                    [styles.manualReturn_cardSelected]: orderId === order.orderId,
                    [styles.manualReturn_cardDisabled]: !selectable,
                  })}
                >
                  <button
                    type="button"
                    disabled={!selectable}
                    className={styles.manualReturn_cardMain}
                    onClick={() => setOrderId(order.orderId)}
                  >
                    <span className={styles.manualReturn_cardOrder}>
                      {order.orderId}
                    </span>
                    <span className={styles.manualReturn_cardMeta}>
                      {orderMeta(order)}
                    </span>
                  </button>

                  <div className={styles.manualReturn_cardFooter}>
                    {/* Why an order cannot be picked, said before the form is
                        filled in rather than after. */}
                    {order.hasReturn ? (
                      <span className={styles.manualReturn_state}>
                        {t("Return already created")}
                      </span>
                    ) : !selectable ? (
                      <span className={styles.manualReturn_expired}>
                        {t("Return window closed")}
                      </span>
                    ) : (
                      <span />
                    )}

                    {/* Somewhere to go rather than a dead end — this is how two
                        tickets ended up on one order during testing. */}
                    <div className={styles.manualReturn_cardActions}>
                      {order.hasReturn && (
                        <Link
                          className={styles.manualReturn_link}
                          to={{
                            pathname: "/returns",
                            search: `returnId=${order.orderId}`,
                          }}
                          target="_blank"
                        >
                          <span>{t("Return")}</span>
                          <Icon
                            as={ArrowIcon}
                            boxSize={14}
                            className={styles.manualReturn_linkArrow}
                            color="#f4318f"
                          />
                        </Link>
                      )}
                      {order.existingTicket && (
                        <Link
                          className={styles.manualReturn_link}
                          to={{
                            pathname: "/ai/tickets",
                            search: `ticketId=${order.existingTicket.id}`,
                          }}
                          target="_blank"
                        >
                          <span>
                            {order.existingTicket.createdByAi
                              ? t("AI ticket")
                              : t("Ticket")}
                          </span>
                          <Icon
                            as={ArrowIcon}
                            boxSize={14}
                            className={styles.manualReturn_linkArrow}
                            color="#f4318f"
                          />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}

          <Input
            label={t("Reason")}
            value={reason}
            maxLength={100}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      )}
    </Modal>
  );
};

export default ManualReturnFlow;
